import { ListOr, Maybe } from '../../../common/data.js';
import { Icon } from '../icons.js';
import { keepExisting } from '../../../common/util.js';
import { MenuEntries, MenuEntry } from '../../../common/data/menus.js';
import { Store } from '../../../common/data/stores.js';
import { VendorUser } from '../../../common/data/vendors.js';
import {
	AccountAccessData,
	AccountPermission,
	OwnAccount,
	permissionsInclude,
} from '../../../common/data/accounts.js';
import { OwnUser } from '../../../common/data/users.js';
import { AccountAvatar } from '../avatar.js';

const filterEmpty = (menus: MenuEntries) => menus.filter(me => !!me.entries?.length);

export const makeUserMenus = ({ user, vendorUsers, account, accountStores, accountAccess }: {
	user: Maybe<OwnUser>;
	vendorUsers: Maybe<VendorUser[]>;
	account: Maybe<OwnAccount>;
	accountStores: Maybe<Store[]>;
	accountAccess: Maybe<AccountAccessData>;
}): MenuEntries => {
	if (!user || !account || !accountAccess) return [];
	const acc = (perm: ListOr<AccountPermission>, me: MenuEntry) => permissionsInclude(accountAccess.permissions, perm) ? me : null;
	return keepExisting([
		vendorUsers?.length ? {
			title: 'Vendor',
			tags: ['vendor'],
			icon: <Icon name="soccer" />,
			entries: vendorUsers.map(vu => ({
				title: `${vu.vendor_name} orders`,
				link: `/account/vendor/${vu.vendor}/orders`,
				icon: <Icon name="circle-dollar" />,
			})),
		} : null,
		{
			title: account.account_name ?? 'Account',
			tags: ['account'],
			icon: <AccountAvatar account={account} />,
			entries: filterEmpty([
				{
					title: 'Account',
					entries: keepExisting([
						{
							title: 'Messages',
							link: `/account/${account.id}/messages`,
							icon: <Icon name="letter" />,
						},
						acc('edit_account_info', {
							title: 'Settings',
							link: `/account/${account.id}/settings`,
							icon: <Icon name="gear" />,
						}),
						acc('view_access', {
							title: 'Team Access',
							link: `/account/${account.id}/users`,
							icon: <Icon name="team" />,
						}),
					]),
				},
				{
					title: 'Purchasing',
					entries: keepExisting([
						acc('own_purchases', {
							title: 'Orders',
							link: `/account/${account.id}/purchases`,
							icon: <Icon name="purchases-logo" />,
						}),
						acc('edit_account_info', { // FIXME
							title: 'Addresses',
							link: `/account/${account.id}/addresses`,
							icon: <Icon name="address" />,
						}),
						acc('own_purchases', { // FIXME
							title: 'Saved Carts',
							link: `/account/${account.id}/saved-carts`,
							icon: <Icon name="cart" />,
						}),
						acc('all_payments', {
							title: 'Invoices',
							link: `/account/${account.id}/invoices`,
							icon: <Icon name="invoice" />,
						}),
						acc('view_purchases', {
							title: 'Purchase Orders',
							link: `/account/${account.id}/purchase-order-application`,
							icon: <Icon name="purchase-order" />,
							tags: ['hidden'],
						}),
						acc('view_payment_methods', {
							title: 'Payment Methods',
							link: `/account/${account.id}/payment-methods`,
							icon: <Icon name="cards" />,
						}),
						acc('own_payments', {
							title: 'Payments',
							link: `/account/${account.id}/payments`,
							icon: <Icon name="circle-dollar" />,
							tags: ['hidden'],
						}),
						acc('assign_budget', {
							title: 'Budgets',
							link: `/account/${account.id}/budgets`,
							icon: <Icon name="budgets" />,
						}),
					]),
				},
				{
					title: 'Assets',
					entries: keepExisting([
						acc('view_logos', {
							title: 'Logos',
							link: `/account/${account.id}/all`,
							icon: <Icon name="all-logo" />,
						}),
						acc('view_logos', {
							title: 'Designs',
							link: `/account/${account.id}/designs`,
							icon: <Icon name="design-logo" />,
						}),
						acc('view_rosters', {
							title: 'Rosters',
							link: `/account/${account.id}/rosters`,
							icon: <Icon name="account-logo" />,
						}),
						// {
						// 	title: 'Purchased',
						// 	link: '#',
						// 	icon: <Icon name="purchases" />,
						// },
					]),
				},
				{
					title: 'Reports',
					entries: keepExisting([
						(account.is_league || account.is_sales_rep) && acc('view_affiliates', {
							title: 'Enrollment',
							link: `/account/${account.id}/members`,
							icon: <Icon name="team" />,
						}) || null,
						acc('all_commissions', {
							title: 'Discounts',
							link: `/account/${account.id}/commissions`,
							icon: <Icon name="commission" />,
						}),
						acc('all_commissions', {
							title: 'Projections',
							link: `/account/${account.id}/rolling`,
							icon: <Icon name="projections" />,
						}),
						acc('all_commissions', {
							title: 'History',
							link: `/account/${account.id}/rolling-history`,
							icon: <Icon name="history" />,
						}),
						acc('view_promocodes', {
							title: 'Affiliate Link',
							link: `/account/${account.id}/links`,
							icon: <Icon name="chain" />,
						}),
						acc('all_commissions', {
							title: 'Payouts',
							link: `/account/${account.id}/payouts`,
							icon: <Icon name="earning-logo" />,
						}),
					]),
				},
			]),
		},
		permissionsInclude(accountAccess.permissions, 'view_store') ? {
			title: 'Stores',
			tags: ['stores'],
			icon: <Icon name="product" />,
			entries: [
				...(accountStores ?? []).map(s => ({
					title: s.name ?? 'Web Store',
					entries: [
						{
							title: 'Setup',
							link: `/account/store/${s.id}/setup`,
							icon: <Icon name="store-setup-logo" />,
						},
						{
							title: 'Pricing',
							link: `/account/store/${s.id}/pricing`,
							icon: <Icon name="commission" />,
						},
						{
							title: 'Draft',
							link: `/account/store/${s.id}/drafts`,
							icon: <Icon name="draft-logo" />,
						},
						{
							title: 'Promocodes',
							link: `/account/store/${s.id}/promocodes`,
							icon: <Icon name="store-promocodes" />,
						},
						{
							title: 'Sales Orders',
							link: `/account/store/${s.id}/orders`,
							icon: <Icon name="sales-order" />,
						},
						{
							title: 'Products',
							link: `/account/store/${s.id}/products`,
							icon: <Icon name="product" />,
						},
						{
							title: 'Visit',
							link: s.full_url ?? `/store/${s.url ?? s.id}`,
							icon: <Icon name="pop-out" />,
							tags: ['pop-out'],
						},
					],
				})),
				{
					title: 'Create a Store',
					link: `/account/store/create`,
				},
			],
		} : permissionsInclude(accountAccess.permissions, 'budget_purchase') ? {
			title: 'Stores',
			tags: ['stores'],
			entries: [
				...(accountStores ?? []).map(s => ({
					title: s.name ?? 'Web Store',
					link: s.full_url ?? `/store/${s.url ?? s.id}`,
				})),
			],
		} : null,
		{
			title: user.email,
			tags: ['user'],
			icon: <Icon name="email" />,
			entries: [
				{
					title: 'User Settings',
					link: '/account/user/settings',
				},
				{
					title: 'All Accounts',
					link: '/account/user/accounts',
				},
			],
		},
	] as MenuEntries);
};
